import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';

function toDateInput(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMin(v) {
  if (v == null || Number.isNaN(v)) return '—';
  if (v < 60) return `${v} min`;
  if (v < 1440) {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  }
  const d = Math.floor(v / 1440);
  const h = Math.floor((v % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v}%`;
}

function corSla(v) {
  if (v == null) return 'var(--portal-muted)';
  if (v >= 90) return '#4CB553';
  if (v >= 70) return '#F5A623';
  return '#b43737';
}



// Volume por hora do dia — onde está o pico de trabalho.
//
// Barras e não linha de propósito: hora do dia é categoria (24 baldes), não uma
// série contínua; barra deixa comparar "às 7h entra o dobro das 11h" de relance.
// Conta MENSAGEM RECEBIDA, não conversa aberta: o esforço acontece quando o
// cliente escreve.
function VolumePorHoraSVG({ dados }) {
  const horas = Array.isArray(dados) ? dados : [];
  const max = Math.max(1, ...horas.map((h) => h.mensagens));
  const total = horas.reduce((a, h) => a + h.mensagens, 0);
  if (!total) {
    return (
      <div style={{ padding: 20, color: 'var(--portal-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
        Sem mensagens no período.
      </div>
    );
  }
  const pico = horas.reduce((a, h) => (h.mensagens > a.mensagens ? h : a), horas[0]);
  const w = 720; const h = 190; const padL = 34; const padB = 26; const padT = 10;
  const larguraBarra = (w - padL - 8) / 24;
  const alturaUtil = h - padB - padT;

  return (
    <>
      <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 10 }}>
        Pico às <strong style={{ color: 'var(--portal-text)' }}>{String(pico.hora).padStart(2, '0')}h</strong>
        {' '}· {pico.mensagens} mensagens recebidas · {total} no período
      </div>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', minWidth: 480, height: 'auto', display: 'block' }}
          role="img" aria-label={`Volume por hora. Pico às ${pico.hora} horas com ${pico.mensagens} mensagens.`}>
          {/* três linhas de referência: dão escala sem poluir */}
          {[0, 0.5, 1].map((f) => {
            const y = padT + alturaUtil * (1 - f);
            return (
              <g key={f}>
                <line x1={padL} x2={w - 4} y1={y} y2={y} stroke="var(--portal-border)" strokeWidth="0.6" />
                <text x={padL - 5} y={y + 3} textAnchor="end" fontSize="9" fill="var(--portal-muted)">
                  {Math.round(max * f)}
                </text>
              </g>
            );
          })}
          {horas.map((hh) => {
            const altura = (hh.mensagens / max) * alturaUtil;
            const x = padL + hh.hora * larguraBarra;
            const ehPico = hh.hora === pico.hora;
            return (
              <g key={hh.hora}>
                <title>{`${String(hh.hora).padStart(2, '0')}h — ${hh.mensagens} mensagens em ${hh.conversas} conversa(s)`}</title>
                <rect
                  x={x + 1.5} y={padT + alturaUtil - altura}
                  width={Math.max(2, larguraBarra - 3)} height={Math.max(altura, hh.mensagens ? 1 : 0)}
                  rx="2"
                  fill={ehPico ? '#3D8B37' : 'rgba(61,139,55,0.42)'}
                />
                {/* rótulo de hora de 3 em 3 pra não embolar no celular */}
                {hh.hora % 3 === 0 && (
                  <text x={x + larguraBarra / 2} y={h - 8} textAnchor="middle" fontSize="9" fill="var(--portal-muted)">
                    {String(hh.hora).padStart(2, '0')}h
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </>
  );
}

// Aderência ao sistema: o time está trabalhando DENTRO do Fio ou ainda pelo app?
//
// A barra compara o trabalho HUMANO (o bot fica de fora — não é escolha de
// ninguém). Embaixo, o uso das ferramentas que só existem aqui: consulta do
// cliente no ERP e Extrato para Conferência. É o que responde "ela está usando
// mesmo?" em vez de "ela está logada?".
function AderenciaCard({ ad }) {
  if (!ad) return null;
  const humano = (ad.pelo_fio || 0) + (ad.pelo_app || 0);
  if (!humano) {
    return (
      <div style={{ padding: 16, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
        Nenhuma mensagem enviada no período.
      </div>
    );
  }
  const corDe = (v) => (v >= 80 ? '#3D8B37' : v >= 50 ? '#F5A623' : '#dc3545');
  const Rot = ({ children }) => (
    <div style={{
      fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--portal-muted)',
    }}>{children}</div>
  );

  // DUAS medidas, porque uma sozinha engana. "Por conversa" é alcance (tocou no
  // Fio ao menos uma vez); "por mensagem" é o esforço real. Nos dados de 14/08
  // dão 53% e 26% — as duas verdadeiras, dizendo coisas diferentes.
  const Medida = ({ rotulo, perc, sub }) => (
    <div style={{ flex: '1 1 190px', minWidth: 170 }}>
      <Rot>{rotulo}</Rot>
      <strong style={{ fontSize: '1.55rem', color: corDe(perc), display: 'block', marginTop: 2 }}>
        {perc == null ? '—' : `${perc}%`}
      </strong>
      <div style={{
        display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden',
        background: 'var(--portal-border)', margin: '5px 0 4px',
      }}>
        <div style={{ width: `${perc || 0}%`, background: corDe(perc) }} />
      </div>
      <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>{sub}</div>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <Medida rotulo="Conversas que passaram pelo Fio" perc={ad.perc_conversas}
          sub={`${ad.conv_fio} de ${ad.conv_total} conversas respondidas`} />
        <Medida rotulo="Respostas escritas no Fio" perc={ad.perc_fio}
          sub={`${ad.pelo_fio} pelo Fio · ${ad.pelo_app} pelo app do celular`} />
      </div>

      {/* Por fila: é aqui que aparece quem adotou e quem não — a média esconde
          que uma equipe está em 72% e outra em 13%. */}
      {ad.por_fila?.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--portal-border)', paddingTop: 12 }}>
          <Rot>Por fila — respostas escritas no Fio</Rot>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {ad.por_fila.map((f) => (
              <div key={f.fila} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  flex: '0 0 38%', maxWidth: 200, fontSize: '0.82rem', color: 'var(--portal-text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{f.fila}</span>
                <div style={{
                  flex: 1, height: 9, borderRadius: 5, overflow: 'hidden',
                  background: 'var(--portal-border)', minWidth: 50,
                }}>
                  <div style={{ width: `${f.perc || 0}%`, height: '100%', background: corDe(f.perc) }} />
                </div>
                <span style={{
                  flex: '0 0 104px', textAlign: 'right', fontSize: '0.8rem',
                  fontVariantNumeric: 'tabular-nums', color: corDe(f.perc), fontWeight: 700,
                }}>
                  {f.perc == null ? '—' : `${f.perc}%`}
                  <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}> ({f.pelo_app} fora)</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* As ferramentas que só existem aqui: usar o Fio é também usar isto. */}
      <div style={{
        marginTop: 16, borderTop: '1px solid var(--portal-border)', paddingTop: 12,
        display: 'flex', gap: 26, flexWrap: 'wrap',
      }}>
        <div>
          <Rot>Consultas de cliente</Rot>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--portal-text)' }}>{ad.consultas}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>
            {ad.consultas_pessoas ? `${ad.consultas_pessoas} pessoa(s)` : 'ficha no ERP'}
          </div>
        </div>
        <div>
          <Rot>Extratos</Rot>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--portal-text)' }}>
            {ad.extratos_enviados} enviados
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>{ad.extratos_gerados} gerados</div>
        </div>
        {ad.por_atendente?.length > 0 && (
          <div style={{ flex: '1 1 230px' }}>
            <Rot>Respostas pelo Fio, por pessoa</Rot>
            <div style={{ display: 'flex', gap: '4px 16px', flexWrap: 'wrap', fontSize: '0.82rem', marginTop: 3 }}>
              {ad.por_atendente.map((a) => (
                <span key={a.atendente}>{a.atendente} <strong>{a.pelo_fio}</strong></span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
        Conta só mensagem enviada pela loja — mensagem de cliente não entra.
        {ad.pelo_bot > 0 && ` ${ad.pelo_bot} do bot fora da conta.`}
      </div>
    </>
  );
}

// Card de KPI generico
function KpiCard({ label, valor, tom, hint, ariaLabel }) {
  const cor = tom || '#3D8B37';
  return (
    <article
      className="content-card"
      style={{ padding: 18, borderLeft: `4px solid ${cor}` }}
      aria-label={ariaLabel || label}
    >
      <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 6 }}>
        {label}
      </div>
      <strong style={{ fontSize: '1.7rem', display: 'block', color: 'var(--portal-text)' }}>
        {valor}
      </strong>
      {hint ? (
        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </article>
  );
}

// Linha temporal SVG (recebidas vs encerradas)
function TendenciaSVG({ dados }) {
  if (!dados.length) {
    return (
      <div style={{ padding: 20, color: 'var(--portal-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
        Sem dados no período.
      </div>
    );
  }
  const w = 720;
  const h = 200;
  const padL = 36;
  const padR = 12;
  const padT = 16;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const max = Math.max(1, ...dados.map((d) => Math.max(d.criados, d.resolvidos)));
  const stepX = dados.length > 1 ? innerW / (dados.length - 1) : 0;

  const px = (i) => padL + i * stepX;
  const py = (v) => padT + innerH - (v / max) * innerH;

  const linha = (key) => dados
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${px(i).toFixed(1)} ${py(d[key]).toFixed(1)}`)
    .join(' ');

  // 4 grid lines (incluindo base)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(max * p));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="auto">
      {/* Grid */}
      {ticks.map((t, i) => {
        const y = padT + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y}
              stroke="rgba(143,165,172,0.15)" strokeDasharray="2 4" />
            <text x={padL - 4} y={y + 4} textAnchor="end"
              fontSize="10" fill="var(--portal-muted)">{t}</text>
          </g>
        );
      })}

      {/* Linha abertas */}
      <path d={linha('criados')} fill="none" stroke="#3D8B37" strokeWidth="2" />
      {/* Linha encerradas */}
      <path d={linha('resolvidos')} fill="none" stroke="#4CB553" strokeWidth="2" />

      {/* Pontos + tooltip */}
      {dados.map((d, i) => (
        <g key={i}>
          <circle cx={px(i)} cy={py(d.criados)} r="3" fill="#3D8B37">
            <title>{`${d.dia} — ${d.criados} recebidas`}</title>
          </circle>
          <circle cx={px(i)} cy={py(d.resolvidos)} r="3" fill="#4CB553">
            <title>{`${d.dia} — ${d.resolvidos} encerradas`}</title>
          </circle>
        </g>
      ))}

      {/* Eixo X (datas) — mostra primeira, ultima e o meio */}
      {[0, Math.floor(dados.length / 2), dados.length - 1]
        .filter((i, idx, arr) => arr.indexOf(i) === idx && i >= 0 && i < dados.length)
        .map((i) => (
          <text key={i} x={px(i)} y={h - 8} textAnchor="middle"
            fontSize="10" fill="var(--portal-muted)">
            {dados[i].dia.slice(5)}
          </text>
        ))}

      {/* Legenda */}
      <g transform={`translate(${padL}, ${padT - 4})`}>
        <circle cx="4" cy="-4" r="3" fill="#3D8B37" />
        <text x="12" y="-1" fontSize="10" fill="var(--portal-text)">Recebidas</text>
        <circle cx="84" cy="-4" r="3" fill="#4CB553" />
        <text x="92" y="-1" fontSize="10" fill="var(--portal-text)">Encerradas</text>
      </g>
    </svg>
  );
}

// Cabecalho de coluna clicavel para ordenacao
function Th({ col, label, sortCol, sortDir, onSort, align }) {
  const ativo = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align || 'right',
        whiteSpace: 'nowrap',
        color: ativo ? 'var(--portal-text)' : undefined,
      }}
    >
      {label}
      {ativo
        ? <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
        : <span style={{ marginLeft: 4, opacity: 0.25 }}>↕</span>}
    </th>
  );
}

// Tabela analitica reutilizavel: volume + tempos + % na meta por dimensao
// (fila, filial ou atendente), com ordenacao por qualquer coluna.
function TabelaAnalitica({ titulo, icone, dados, chave, labelChave }) {
  const [sortCol, setSortCol] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    return [...(dados || [])].sort((a, b) => {
      const va = a[sortCol] ?? (sortDir === 'desc' ? -Infinity : Infinity);
      const vb = b[sortCol] ?? (sortDir === 'desc' ? -Infinity : Infinity);
      return sortDir === 'desc' ? vb - va : va - vb;
    });
  }, [dados, sortCol, sortDir]);

  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const thProps = { sortCol, sortDir, onSort };

  if (!dados || dados.length === 0) {
    return (
      <section className="content-card" style={{ padding: 18 }}>
        <h5 style={{ color: 'var(--portal-text)' }}>
          <i className={`bi ${icone} me-2`} /> {titulo}
        </h5>
        <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados no período.</div>
      </section>
    );
  }

  return (
    <section className="content-card" style={{ padding: 18 }}>
      <h5 style={{ color: 'var(--portal-text)', marginBottom: 12 }}>
        <i className={`bi ${icone} me-2`} /> {titulo}
      </h5>
      <div className="table-shell">
        <table className="portal-table">
          <thead>
            <tr>
              <Th col={chave} label={labelChave} align="left" {...thProps} />
              <Th col="total" label="Conversas" {...thProps} />
              <Th col="ttfr_min_medio" label="Tempo 1ª resp." {...thProps} />
              <Th col="ttfr_min_mediana" label="Mediana" {...thProps} />
              <Th col="ttfr_perc" label="% na meta (1ª resp)" {...thProps} />
              <Th col="ttr_min_medio" label="Tempo atend." {...thProps} />
              <Th col="ttr_min_mediana" label="Mediana" {...thProps} />
              <Th col="ttr_perc" label="% na meta (atend)" {...thProps} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row[chave] || row.nome}>
                <td style={{ fontWeight: 500 }}>{row.nome || row[chave]}</td>
                <td style={{ textAlign: 'right' }}>{row.total}</td>
                <td style={{ textAlign: 'right' }}>{formatMin(row.ttfr_min_medio)}</td>
                <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                  {formatMin(row.ttfr_min_mediana)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(row.ttfr_perc) }}>
                  {row.ttfr_avaliados > 0
                    ? <span title={`${row.ttfr_dentro}/${row.ttfr_avaliados} conversas`}>{pct(row.ttfr_perc)}</span>
                    : <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}>—</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{formatMin(row.ttr_min_medio)}</td>
                <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                  {formatMin(row.ttr_min_mediana)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(row.ttr_perc) }}>
                  {row.ttr_avaliados > 0
                    ? <span title={`${row.ttr_dentro}/${row.ttr_avaliados} conversas`}>{pct(row.ttr_perc)}</span>
                    : <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
        Clique em qualquer cabeçalho para ordenar. % na meta calculado apenas para conversas com tempo-meta configurado.
      </div>
    </section>
  );
}

const ABAS_ANALITICA = [
  { chave: 'por_filial',    label: 'Filial',    icone: 'bi-buildings',   labelChave: 'Filial'    },
  { chave: 'por_setor',     label: 'Fila',      icone: 'bi-diagram-3',   labelChave: 'Fila'      },
  { chave: 'por_atendente', label: 'Atendente', icone: 'bi-person-badge',labelChave: 'Atendente' },
];

function VisaoAnalitica({ analise, temMultiFiliais }) {
  const abas = temMultiFiliais
    ? ABAS_ANALITICA
    : ABAS_ANALITICA.filter((a) => a.chave !== 'por_filial');
  const [abaAtiva, setAbaAtiva] = useState(abas[0].chave);
  const aba = abas.find((a) => a.chave === abaAtiva) || abas[0];

  return (
    <div
      style={{
        marginTop: 8,
        border: '1px solid var(--portal-border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Cabecalho */}
      <div style={{
        padding: '12px 18px 0',
        background: 'var(--portal-surface)',
        borderBottom: '1px solid var(--portal-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <i className="bi bi-bar-chart-steps" style={{ color: '#3D8B37' }} />
          <span style={{ fontWeight: 600, color: 'var(--portal-text)' }}>Visão Analítica</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
            Tempo 1ª resp. · Tempo atend. · % na meta
          </span>
        </div>
        {/* Abas */}
        <div style={{ display: 'flex', gap: 2 }}>
          {abas.map((a) => {
            const ativa = a.chave === abaAtiva;
            return (
              <button
                key={a.chave}
                onClick={() => setAbaAtiva(a.chave)}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: ativa ? 600 : 400,
                  fontSize: '0.85rem',
                  background: ativa ? 'var(--portal-bg)' : 'transparent',
                  color: ativa ? '#3D8B37' : 'var(--portal-muted)',
                  borderBottom: ativa ? '2px solid #3D8B37' : '2px solid transparent',
                  transition: 'color 0.15s, border-color 0.15s',
                }}
              >
                <i className={`bi ${a.icone} me-1`} />
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteudo da aba ativa */}
      <div style={{ padding: '12px 16px 16px', background: 'var(--portal-bg)' }}>
        <TabelaAnalitica
          titulo={`Por ${aba.label.toLowerCase()}`}
          icone={aba.icone}
          dados={analise?.[abaAtiva] || []}
          chave={abaAtiva.replace('por_', '')}
          labelChave={aba.labelChave}
        />
      </div>
    </div>
  );
}

function MultiSelect({ placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length ? `${value.length} selecionado(s)` : placeholder;
  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', padding: 0,
          background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem',
          color: value.length ? 'var(--portal-text)' : 'var(--portal-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          outline: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ flexShrink: 0, fontSize: '0.7rem' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, minWidth: '100%',
          maxHeight: 200, overflowY: 'auto', background: 'var(--portal-surface)',
          border: '1px solid var(--portal-border)', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          {options.length === 0
            ? <div style={{ padding: '6px 10px', fontSize: '0.8rem', color: 'var(--portal-muted)' }}>Sem opções</div>
            : options.map((o) => (
              <label key={o.value} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                cursor: 'pointer', fontSize: '0.84rem', color: 'var(--portal-text)',
                background: value.includes(o.value) ? 'rgba(61, 139, 55,0.08)' : 'transparent',
                userSelect: 'none',
              }}>
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} style={{ flexShrink: 0, margin: 0 }} />
                <span>{o.label}</span>
              </label>
            ))}
        </div>
      )}
    </div>
  );
}

export default function Indicadores() {
  const { temPermissao } = useAuth();
  const podeVer = temPermissao('ti:atendimentos', 'admin:atendimento', 'ti:dashboard');

  const hoje = new Date();
  const inicioPadrao = new Date();
  inicioPadrao.setDate(hoje.getDate() - 30);

  const [inicio, setInicio] = useState(toDateInput(inicioPadrao));
  const [fim, setFim] = useState(toDateInput(hoje));
  const [filial, setFilial] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const [filiaisDisp, setFiliaisDisp] = useState([]);
  const [canalId, setCanalId] = useState('');
  const [atendentes, setAtendentes] = useState([]);
  const [canaisDisp, setCanaisDisp] = useState([]);
  const [atendentesDisp, setAtendentesDisp] = useState([]);

  // Carrega catalogos para os filtros (filiais, canais e atendentes)
  useEffect(() => {
    if (!podeVer) return;
    Promise.allSettled([
      axios.get(`${API}/atendimento/filiais?ativos=true`),
      axios.get(`${API}/atendimento/canais`),
      axios.get(`${API}/metricas/atendentes`),
    ]).then(([rf, rcan, rat]) => {
      if (rf.status === 'fulfilled') setFiliaisDisp(rf.value.data?.data || []);
      if (rcan.status === 'fulfilled') setCanaisDisp(rcan.value.data?.data || []);
      if (rat.status === 'fulfilled') setAtendentesDisp((rat.value.data?.data || []).map((a) => ({ value: a, label: a })));
    });
  }, [podeVer]);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = { inicio, fim };
      if (filial) params.filial = filial;
      if (canalId) params.canal_id = canalId;
      if (atendentes.length) params.atendentes = atendentes.join(',');
      const res = await axios.get(`${API}/metricas/atendimento`, { params });
      setData(res.data?.data || null);
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível carregar os indicadores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeVer) return;
    carregar();
  }, []); // carga inicial - filtros sao aplicados pelo botao Aplicar

  const temMultiFiliais = filiaisDisp.length > 1;

  if (!podeVer) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Você não tem permissão para ver os indicadores.
        </div>
      </div>
    );
  }

  const aplicarRapido = (dias) => {
    const ini = new Date();
    ini.setDate(ini.getDate() - dias);
    setInicio(toDateInput(ini));
    setFim(toDateInput(new Date()));
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Atendimento</span>
          <h1>Indicadores</h1>
          <p>Retrospecto do período: rapidez da 1ª resposta, tempo até encerrar, aderência às metas e volume.</p>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field">
          <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>De</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </div>
        <div className="filter-card__field">
          <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Até</label>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
        </div>
        {temMultiFiliais && (
          <div className="filter-card__field">
            <select value={filial} onChange={(e) => setFilial(e.target.value)}>
              <option value="">Todas filiais</option>
              {filiaisDisp.map((f) => (
                <option key={f.id} value={f.nome}>
                  {f.codigo ? `${f.codigo} - ` : ''}{f.nome}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="filter-card__field">
          <select value={canalId} onChange={(e) => setCanalId(e.target.value)}>
            <option value="">Todos os canais</option>
            {canaisDisp.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <MultiSelect
            placeholder="Todos atendentes"
            options={atendentesDisp}
            value={atendentes}
            onChange={setAtendentes}
          />
        </div>
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={carregar}
            style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
            disabled={loading}
          >
            <i className="bi bi-funnel me-1" />
            {loading ? 'Carregando...' : 'Aplicar'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(7)}>7d</button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(30)}>30d</button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(90)}>90d</button>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      {loading ? (
        <div className="content-card content-card--centered">
          <div className="screen-loader__spinner" />
          <p>Carregando indicadores...</p>
        </div>
      ) : !data ? (
        <div className="empty-state">
          <strong>Sem dados</strong>
          <span>Aplique os filtros para ver os indicadores.</span>
        </div>
      ) : (
        <>
          {/* 1. Qualidade em destaque — é o que se revisa depois do período:
              rapidez da 1ª resposta, tempo até encerrar e aderência às metas. */}
          <section className="row g-3" style={{ margin: 0 }}>
            <div className="col-12 col-md-6 col-lg-6">
              <KpiCard label="Tempo até a 1ª resposta"
                valor={formatMin(data.tempos.ttfr_min_medio)}
                tom="#3D8B37"
                hint={`mediana ${formatMin(data.tempos.ttfr_min_mediana)} · ${data.tempos.com_primeira_resposta} avaliada(s)`} />
            </div>
            <div className="col-12 col-md-6 col-lg-6">
              <KpiCard label="Tempo até encerrar"
                valor={formatMin(data.tempos.ttr_min_medio)}
                tom="#4CB553"
                hint={`mediana ${formatMin(data.tempos.ttr_min_mediana)} · ${data.tempos.com_resolucao} encerrada(s)`} />
            </div>
          </section>

          {/* 2. Volume do período — contexto compacto, não é o herói da tela. */}
          <section className="content-card" style={{ padding: '12px 20px', marginTop: 4, display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>{data.totais.criados}</strong>
              <span style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>conversas recebidas no período</span>
            </div>
            <div style={{ width: 1, height: 26, background: 'var(--portal-border)' }} />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>{data.totais.resolvidos}</strong>
              <span style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>
                encerradas{data.totais.criados ? ` · ${Math.round((data.totais.resolvidos / data.totais.criados) * 100)}% do total` : ''}
              </span>
            </div>
          </section>

          {/* 3. Aderência ao sistema: está trabalhando no Fio ou ainda no app? */}
          <section className="content-card" style={{ padding: 18, marginTop: 4 }}>
            <h4 style={{ marginBottom: 4, color: 'var(--portal-text)' }}>
              <i className="bi bi-shield-check me-2" /> Aderência ao sistema
            </h4>
            <p style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', marginBottom: 12 }}>
              Quanto do atendimento passou pelo Fio e quanto saiu direto do app do celular,
              mais o uso das ferramentas que só existem aqui.
            </p>
            <AderenciaCard ad={data.aderencia} />
          </section>

          {/* 4. Os dois gráficos dividem a linha: cada um conta metade da
              história (quando o cliente chega x como o período evolui), e
              nenhum precisa da tela inteira pra ser lido. No celular empilham. */}
          <section className="row g-3" style={{ margin: 0, marginTop: 4 }}>
            <div className="col-12 col-lg-6">
              <div className="content-card" style={{ padding: 16, height: '100%' }}>
                <h4 style={{ marginBottom: 2, color: 'var(--portal-text)', fontSize: '1rem' }}>
                  <i className="bi bi-bar-chart me-2" /> Volume por horário
                </h4>
                <p style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                  Mensagens recebidas por hora — quando o cliente escreve.
                </p>
                <VolumePorHoraSVG dados={data.por_hora} />
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="content-card" style={{ padding: 16, height: '100%' }}>
                <h4 style={{ marginBottom: 2, color: 'var(--portal-text)', fontSize: '1rem' }}>
                  <i className="bi bi-graph-up me-2" /> Tendência diária
                </h4>
                <p style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                  Recebidas x encerradas ao longo do período.
                </p>
                <TendenciaSVG dados={data.tendencia} />
              </div>
            </div>
          </section>

          {/* 5. Visao Analitica: a mesa unica por filial/fila/atendente
              (volume + tempos + % na meta, ordenavel) */}
          <VisaoAnalitica analise={data.analise} temMultiFiliais={temMultiFiliais} />

          {/* Legenda - explica cada termo. Fica recolhida por padrao para nao
              poluir, mas sempre acessivel para quem nao conhece os indicadores. */}
          <details
            style={{
              marginTop: 8,
              padding: '10px 14px',
              border: '1px dashed var(--portal-border)',
              borderRadius: 8,
              background: 'transparent',
              fontSize: '0.82rem',
              color: 'var(--portal-muted)',
            }}
          >
            <summary style={{ cursor: 'pointer', userSelect: 'none', color: 'var(--portal-text)' }}>
              <i className="bi bi-info-circle me-2" />
              O que cada indicador significa?
            </summary>
            <dl style={{
              marginTop: 12,
              marginBottom: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '6px 14px',
              lineHeight: 1.5,
            }}>
              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Conversas recebidas</dt>
              <dd style={{ margin: 0 }}>Conversas (atendimentos) que chegaram no período selecionado.</dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Encerradas</dt>
              <dd style={{ margin: 0 }}>Dessas conversas do período, quantas já foram encerradas.</dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Tempo até a 1ª resposta</dt>
              <dd style={{ margin: 0 }}>
                Tempo entre a chegada da conversa e a primeira resposta de um atendente (mensagens
                do bot não contam). Conta só o tempo dentro do horário de atendimento configurado:
                se o cliente escreve 17:58 e o atendente responde 07:00 do dia seguinte com horário
                07-18, o tempo é 2 min, não 13h. <em>(também chamado de TTFR)</em>
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Tempo até encerrar</dt>
              <dd style={{ margin: 0 }}>
                Tempo entre a chegada e o encerramento da conversa. Mesma regra do horário de
                atendimento. <em>(TTR)</em>
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Mediana</dt>
              <dd style={{ margin: 0 }}>
                Valor que divide a amostra ao meio; menos sensível a casos extremos que a média.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>% dentro da meta</dt>
              <dd style={{ margin: 0 }}>
                Das conversas avaliadas, quantas % ficaram dentro do tempo-meta configurado (em
                <em> /admin/atendimento</em>). Verde &ge; 90%, laranja entre 70% e 89%, vermelho abaixo.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Avaliadas</dt>
              <dd style={{ margin: 0 }}>
                Quantas conversas têm dado suficiente para o cálculo (ex: tempo até a 1ª resposta
                só conta conversas que já receberam resposta).
              </dd>
            </dl>
          </details>
        </>
      )}
    </div>
  );
}
